import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MembershipSettingsView } from "@/modules/membership/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CakeIcon, CrownIcon, SaveIcon, SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { membershipApi } from "../api/client";
import { membershipKeys } from "../api/queries";
import { EligibleMenuScopePicker } from "../ui/EligibleMenuScopePicker";

const RESET_MODE_LABELS = {
  CALENDAR_MONTH: "ຕົ້ນເດືອນປະຕິທິນ",
  ROLLING_30_DAYS: "Rolling 30 ວັນ",
  TIER_ANNIVERSARY: "ຄົບຮອບບັດ (30 ວັນ)",
} as const;

const BIRTHDAY_TIER_OPTIONS = [
  { value: "NONE" as const, label: "ສະມາຊິກທົ່ວໄປ (NONE)" },
  { value: "SILVER" as const, label: "ບັດເງິນ (Silver)" },
  { value: "GOLD" as const, label: "ບັດຄຳ (Gold)" },
];

const MEMBERSHIP_SETTINGS_BREADCRUMB = [
  {
    label: "ບັດສະມາຊິກ",
    to: "/app/membership/settings",
  },
] as const;

export function MembershipSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: membershipKeys.settings(),
    queryFn: () => membershipApi.getSettings(),
  });

  const [form, setForm] = useState<MembershipSettingsView | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<MembershipSettingsView>) =>
      membershipApi.updateSettings(payload),
    onSuccess: (updated) => {
      setForm(updated);
      toast.success("ບັນທຶກການຕັ້ງຄ່າບັດສະມາຊິກແລ້ວ");
      qc.invalidateQueries({ queryKey: membershipKeys.settings() });
    },
  });

  if (isLoading || !form) {
    return (
      <>
        <Header>
          <Breadcrumb items={[...MEMBERSHIP_SETTINGS_BREADCRUMB]} />
        </Header>
        <Main>
          <Loader />
        </Main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header>
          <Breadcrumb items={[...MEMBERSHIP_SETTINGS_BREADCRUMB]} />
        </Header>
        <Main>
          <p className="text-destructive text-sm">ໂຫຼດການຕັ້ງຄ່າບໍ່ສຳເລັດ</p>
        </Main>
      </>
    );
  }

  const handleSave = () => {
    if (
      form.eligibleMenu.scopeType === "PRODUCT" &&
      form.eligibleMenu.scopeIds.length === 0
    ) {
      toast.error("ກະລຸນາເລືອກເມນູຢ່າງໜ້ອຍ 1 ສິນຄ້າ");
      return;
    }
    if (
      form.eligibleMenu.scopeType === "VARIANT" &&
      form.eligibleMenu.scopeIds.length === 0
    ) {
      toast.error("ກະລຸນາເລືອກຢ່າງໜ້ອຍ 1 ແບບ (variant)");
      return;
    }
    if (form.birthday.eligibleTiers.length === 0) {
      toast.error("ກະລຸນາເລືອກຢ່າງໜ້ອຍ 1 ລະດັບສຳລັບສິດວັນເກີດ");
      return;
    }
    if (
      form.birthday.pushDaysBeforeBirthday >
      form.birthday.windowDaysBefore
    ) {
      toast.error(
        "Push ກ່ອນວັນເກີດຕ້ອງ ≤ window ກ່ອນວັນເກີດ (windowDaysBefore)",
      );
      return;
    }
    void saveMutation.mutateAsync(form);
  };

  return (
    <>
      <Header>
        <Breadcrumb items={[...MEMBERSHIP_SETTINGS_BREADCRUMB]} />
      </Header>
      <Main>
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              ຕັ້ງຄ່າບັດສະມາຊິກ
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Auto upgrade ຈາກ scan, quota ຟຣີລາຍເດືອນ, ສ່ວນຫຼຸດ tier
            </p>
          </div>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="gap-2"
          >
            <SaveIcon className="size-4" />
            ບັນທຶກ
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <SparklesIcon className="text-amber-600 size-5" />
              <div>
                <h2 className="font-semibold">Auto Upgrade (Scan)</h2>
                <p className="text-muted-foreground text-xs">
                  ຈຳນວນຄັ້ງທີ່ສະແກນເພື່ອຂຶ້ນ tier
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">NONE → SILVER</span>
                <Input
                  type="number"
                  min={1}
                  value={form.rules.noneToSilverVisits}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rules: {
                        ...form.rules,
                        noneToSilverVisits: Number(e.target.value) || 1,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">SILVER → GOLD</span>
                <Input
                  type="number"
                  min={1}
                  value={form.rules.silverToGoldVisits}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rules: {
                        ...form.rules,
                        silverToGoldVisits: Number(e.target.value) || 1,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Visit window (ວັນ, 0=lifetime)</span>
                <Input
                  type="number"
                  min={0}
                  value={form.rules.visitWindowDays}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rules: {
                        ...form.rules,
                        visitWindowDays: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <CrownIcon className="text-yellow-600 size-5" />
              <div>
                <h2 className="font-semibold">Quota ຟຣີລາຍເດືອນ</h2>
                <p className="text-muted-foreground text-xs">
                  ຈຳນວນແກ້ວຟຣີຕໍ່ລູກຄ້າ
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Silver (ແກ້ວ/ຮອບ)</span>
                <Input
                  type="number"
                  min={0}
                  value={form.quota.silverMonthly}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      quota: {
                        ...form.quota,
                        silverMonthly: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Gold (ແກ້ວ/ຮອບ)</span>
                <Input
                  type="number"
                  min={0}
                  value={form.quota.goldMonthly}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      quota: {
                        ...form.quota,
                        goldMonthly: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Reset mode</span>
                <Select
                  value={form.quota.resetMode}
                  onValueChange={(value) =>
                    setForm({
                      ...form,
                      quota: {
                        ...form.quota,
                        resetMode: value as MembershipSettingsView["quota"]["resetMode"],
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RESET_MODE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="font-semibold">ສ່ວນຫຼຸດ Tier (%)</h2>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Silver</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.discounts.silverPercent}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      discounts: {
                        ...form.discounts,
                        silverPercent: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Gold</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.discounts.goldPercent}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      discounts: {
                        ...form.discounts,
                        goldPercent: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center gap-2">
              <CakeIcon className="text-pink-600 size-5" />
              <div>
                <h2 className="font-semibold">ວັນເກີດຟຣີ</h2>
                <p className="text-muted-foreground text-xs">
                  ຊ່ວງໃຊ້ສິດ ແລະ ເວລາສົ່ງ push
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <span className="text-sm font-medium">
                  ລະດັບທີ່ໄດ້ສິດວັນເກີດຟຣີ
                </span>
                <div className="flex flex-col gap-2 rounded-lg border border-border/70 p-3">
                  {BIRTHDAY_TIER_OPTIONS.map((opt) => {
                    const checked = form.birthday.eligibleTiers.includes(
                      opt.value,
                    );
                    return (
                      <label
                        key={opt.value}
                        htmlFor={`birthday-tier-${opt.value}`}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <Checkbox
                          id={`birthday-tier-${opt.value}`}
                          checked={checked}
                          onCheckedChange={(val) => {
                            const next = new Set(form.birthday.eligibleTiers);
                            if (val === true) next.add(opt.value);
                            else next.delete(opt.value);
                            if (next.size === 0) {
                              toast.error(
                                "ຕ້ອງເຫຼືອຢ່າງໜ້ອຍ 1 ລະດັບ",
                              );
                              return;
                            }
                            const order = ["NONE", "SILVER", "GOLD"] as const;
                            setForm({
                              ...form,
                              birthday: {
                                ...form.birthday,
                                eligibleTiers: order.filter((t) =>
                                  next.has(t),
                                ),
                              },
                            });
                          }}
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-muted-foreground text-xs">
                  ເລືອກ tier ທີ່ລູກຄ້າໄດ້ຮັບ entitlement ວັນເກີດ + push
                </p>
              </div>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Window ກ່ອນວັນເກີດ (ວັນ)</span>
                <Input
                  type="number"
                  min={0}
                  value={form.birthday.windowDaysBefore}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      birthday: {
                        ...form.birthday,
                        windowDaysBefore: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Window ຫຼັງວັນເກີດ (ວັນ)</span>
                <Input
                  type="number"
                  min={0}
                  value={form.birthday.windowDaysAfter}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      birthday: {
                        ...form.birthday,
                        windowDaysAfter: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">Push hour (local, 0–23)</span>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={form.birthday.pushHourLocal}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      birthday: {
                        ...form.birthday,
                        pushHourLocal: Math.min(
                          23,
                          Math.max(0, Number(e.target.value) || 0),
                        ),
                      },
                    })
                  }
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium">
                  Push FCM ກ່ອນວັນເກີດ (ວັນ)
                </span>
                <Input
                  type="number"
                  min={0}
                  max={30}
                  value={form.birthday.pushDaysBeforeBirthday}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      birthday: {
                        ...form.birthday,
                        pushDaysBeforeBirthday: Math.max(
                          0,
                          Number(e.target.value) || 0,
                        ),
                      },
                    })
                  }
                />
                <p className="text-muted-foreground text-xs">
                  0 = ບໍ່ສົ່ງ push · ຄ່າເລີ່ມຕົ້ນ 2 (ຕ້ອງ ≤ window ກ່ອນວັນເກີດ)
                </p>
              </label>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <h2 className="font-semibold">ເມນູທີ່ເລືອກໄດ້ (eligible)</h2>
              <p className="text-muted-foreground text-sm">
                ລູກຄ້າເລືອກເມນູຈາກລາຍການນີ້ເມື່ອໃຊ້ quota / ວັນເກີດ / launch
              </p>
            </CardHeader>
            <CardContent>
              <EligibleMenuScopePicker
                value={form.eligibleMenu}
                onChange={(eligibleMenu) =>
                  setForm({
                    ...form,
                    eligibleMenu,
                  })
                }
              />
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
